import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Mail, Send, Clock,
  LayoutDashboard, Package, CheckCircle, Loader2, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Emails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState('welcome');
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchScheduledEmails();
  }, []);

  const fetchScheduledEmails = async () => {
    try {
      const res = await axios.get(`${API}/email/scheduled`);
      setScheduledEmails(res.data);
    } catch (err) {
      console.error('Failed to fetch scheduled emails:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/email/send-test`, {
        email: testEmail,
        template: previewTemplate,
        name: user?.name || 'Test User',
        expertise: 'Estimator'
      });
      
      if (res.data.status === 'sent') {
        toast.success('Test email sent!');
      } else if (res.data.status === 'skipped') {
        toast.error('Email not sent: ' + res.data.reason);
      } else {
        toast.error('Failed to send: ' + res.data.error);
      }
    } catch (err) {
      toast.error('Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const handleProcessEmails = async () => {
    try {
      await axios.post(`${API}/email/process`);
      toast.success('Processing scheduled emails...');
      setTimeout(fetchScheduledEmails, 2000);
    } catch (err) {
      toast.error('Failed to process emails');
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: MessageSquare, label: 'Pitch Generator', path: '/pitch' },
    { icon: Package, label: 'Workflows', path: '/workflows' },
    { icon: Mail, label: 'Emails', path: '/emails' },
  ];

  const templates = [
    { id: 'welcome', name: 'Welcome Email', day: 'Immediate' },
    { id: 'day1', name: 'First 3 Steps', day: 'Day 1' },
    { id: 'day3', name: 'Success Stories', day: 'Day 3' },
    { id: 'day7', name: 'Upgrade Prompt', day: 'Day 7' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 flex items-center justify-center">
                <Zap className="w-5 h-5 text-black" />
              </div>
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive 
                      ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' 
                      : 'text-muted-foreground hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { logout(); navigate('/'); }}
              className="w-full justify-start text-muted-foreground hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold">Email Onboarding</h1>
            <span className="text-xs font-mono px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              {scheduledEmails.length} SCHEDULED
            </span>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-8">
          {/* Test Email Section */}
          <div className="bg-[#0a0a0a] border border-white/5 p-6">
            <h2 className="font-bold mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-400" />
              Send Test Email
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-2 block">Template</label>
                <Select value={previewTemplate} onValueChange={setPreviewTemplate}>
                  <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} ({t.day})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-2 block">Send To</label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="bg-black/50 border-white/10 rounded-sm"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleSendTest}
                  disabled={sending}
                  className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-10"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send Test</>}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Note: Requires RESEND_API_KEY in backend/.env. In test mode, emails only go to verified addresses.
            </p>
          </div>

          {/* Onboarding Sequence */}
          <div className="bg-[#0a0a0a] border border-white/5">
            <div className="p-6 border-b border-white/5">
              <h2 className="font-bold">Onboarding Email Sequence</h2>
              <p className="text-sm text-muted-foreground mt-1">4 emails sent automatically after signup</p>
            </div>
            <div className="divide-y divide-white/5">
              {templates.map((template, i) => (
                <div key={template.id} className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center ${
                      i === 0 ? 'bg-emerald-500/10 text-emerald-400' : 
                      i === 1 ? 'bg-blue-500/10 text-blue-400' :
                      i === 2 ? 'bg-violet-500/10 text-violet-400' :
                      'bg-amber-500/10 text-amber-400'
                    }`}>
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">{template.name}</p>
                      <p className="text-sm text-muted-foreground">{template.day}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewTemplate(template.id)}
                    className="text-muted-foreground hover:text-emerald-400"
                  >
                    <Eye className="w-4 h-4 mr-1" /> Preview
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Scheduled Emails */}
          <div className="bg-[#0a0a0a] border border-white/5">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <div>
                <h2 className="font-bold">Your Scheduled Emails</h2>
                <p className="text-sm text-muted-foreground mt-1">Emails queued for your account</p>
              </div>
              <Button
                onClick={handleProcessEmails}
                variant="outline"
                className="border-white/10"
              >
                <Clock className="w-4 h-4 mr-2" /> Process Now
              </Button>
            </div>
            <div className="divide-y divide-white/5">
              {scheduledEmails.length > 0 ? scheduledEmails.map((email, i) => (
                <div key={email.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {email.sent ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Clock className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{email.subject}</p>
                      <p className="text-xs text-muted-foreground">
                        {email.sent ? `Sent ${new Date(email.sent_at).toLocaleString()}` : `Scheduled for ${new Date(email.scheduled_at).toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-mono ${email.sent ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                    {email.sent ? 'SENT' : 'PENDING'}
                  </span>
                </div>
              )) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No scheduled emails yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Emails;
